import { useEffect, useState } from "react";
import { useLocation, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import { Autocomplete, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Button from "../ui/Button";
import useLoader from "../../hooks/useLoader";
import {
    generateCompliance,
    generateLwfStatutoryForm,
    generateFormXVII,
    generateProfessionTaxStatement,
    generateForm24Q,
    generateMaternityFormA,
    getForm12BEmployees,
    generateForm12B,
    getComplianceFrequency,
    getStates,
    generateBonusFormC,
    generateBonusFormD,
    generateGratuityFormF,
    generateForm15,
    generateApprenticesAct,
    generateShopsEstablishmentForm,
} from "../../services/compliance";
import { getUsersByStatus } from "../../services/organizationHierarchy";
import { formatDateToYYYYMMDD } from "../../Helper/functionHelper";

const EMPLOYEE_STATUS_OPTIONS = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "all", label: "All" },
];

// Shared by Bonus Form C, Gratuity Form F and Form 15 - the single Employee
// dropdown these reports use carries "All Employees" as its first option
// (not a separate checkbox/select), so it's a plain sentinel row prepended
// ahead of the real employees fetched via getUsersByStatus().
const ALL_EMPLOYEES_OPTION = {
    _UserID: "ALL",
    _FirstName: "All Employees",
    _MiddleName: "",
    _LastName: "",
    _UserEmployeeID: "",
};

// pms_compliance_master.frequency: 1=monthly, 3=quarterly, 6=half-yearly, 12=yearly
const MONTHLY_FREQUENCY = "1";
const QUARTERLY_FREQUENCY = "3";
const HALF_YEARLY_FREQUENCY = "6";
const YEARLY_FREQUENCY = "12";

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

const QUARTER_OPTIONS = [
    { value: "1", label: "1st Quarter (Jan - Mar)" },
    { value: "2", label: "2nd Quarter (Apr - Jun)" },
    { value: "3", label: "3rd Quarter (Jul - Sep)" },
    { value: "4", label: "4th Quarter (Oct - Dec)" },
];

const HALF_YEAR_OPTIONS = [
    { value: "1", label: "1st Half-Year (Jan - Jun)" },
    { value: "2", label: "2nd Half-Year (Jul - Dec)" },
];

// Form 24Q (TDS on Salaries) files by *financial-year* quarter, not calendar
// quarter - Q1 starts in April, not January - so it gets its own labels
// rather than reusing LWF's QUARTER_OPTIONS above.
const FORM24Q_QUARTER_OPTIONS = [
    { value: "1", label: "Q1 (Apr - Jun)" },
    { value: "2", label: "Q2 (Jul - Sep)" },
    { value: "3", label: "Q3 (Oct - Dec)" },
    { value: "4", label: "Q4 (Jan - Mar)" },
];

// Every newly-added LWF state/form (see Config/lwfStateForm.js's
// STATE_IDS.GOA/WEST_BENGAL/KERALA/ANDHRA_PRADESH/TAMIL_NADU/TELANGANA and
// their resolve*Form() functions), keyed by state id then by the form letter
// each state's own form_name starts with (matches the exact same per-state,
// per-letter identity those resolve*Form() functions already use - e.g.
// resolveKeralaForm's /\bform a\b/ / /\bform b\b/ / /\bform c\b/). The value
// is the filter shape that form needs:
//   "half-year"  -> Year + Half-Year (Goa Form A, West Bengal Form D/E,
//                   Kerala Form A)
//   "month-year" -> Month-Year + Employee Status + Employee (Goa Form D,
//                   West Bengal Form G, Kerala Form B)
//   "year"       -> Year only, no Employee fields (Andhra Pradesh Form E/F,
//                   Tamil Nadu Form A, Telangana Form E/F, Kerala Form C)
// A single lookup table instead of one flag per form, since several of these
// states mix more than one shape (Kerala has all three) - the state's own
// pms_compliance_master filing frequency can only hold one value, so it
// can't be relied on to distinguish them; see isNewLwfStateForm/
// newLwfFormShape below.
const NEW_LWF_FORM_SHAPES = {
    1484: { a: "half-year", d: "month-year" }, // Goa
    1506: { d: "half-year", e: "half-year", g: "month-year" }, // West Bengal
    1490: { a: "half-year", b: "month-year", c: "year" }, // Kerala
    1476: { e: "year", f: "year" }, // Andhra Pradesh
    1503: { a: "year" }, // Tamil Nadu
    4231: { e: "year", f: "year" }, // Telangana
};

// Pulls the single form-code letter (A, B, C, ...) every new state's
// form_name starts with, e.g. "Form G - Register of wages - ..." -> "g".
function detectNewLwfFormLetter(formName) {
    const match = /\bform\s+([a-z])\b/i.exec(formName || "");
    return match ? match[1].toLowerCase() : null;
}

const ComplianceGenerate = () => {
    const { state } = useLocation();
    const {
        form,
        type,
        label,
        state: preselectedState,
        formId,
        filters: seFilters = [],
    } = state || {};
    const { monthYear } = useOutletContext();
    const { showLoader, hideLoader } = useLoader();

    const [states, setStates] = useState([]);
    const [complianceState, setComplianceState] = useState(preselectedState || "");
    const [employeeStatus, setEmployeeStatus] = useState("all");
    const [lwfFrequency, setLwfFrequency] = useState("");
    const [period, setPeriod] = useState({ year: "", quarter: "", half: "" });
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [form12BEmployees, setForm12BEmployees] = useState([]);
    const [selectedForm12BEmployees, setSelectedForm12BEmployees] = useState([]);
    // Bonus Form C/D's From Month/To Month range (native month pickers, same
    // as PayrollMonthFilter's <input type="month">, not a new date-range
    // component). `selectedEmployee` (null/ALL = "All Employees") is shared
    // by Form C, Gratuity Form F and Form 15's Employee picker below.
    const [fromMonth, setFromMonth] = useState("");
    const [toMonth, setToMonth] = useState("");
    // Form 15's own filter: a plain calendar Year (not a financial year -
    // Form 15 is filed year-wise, Jan-Dec) reusing the same YEAR_OPTIONS
    // dropdown Form 12B already uses. Defaults to the current year (never
    // hardcoded) but stays changeable via the dropdown below.
    const [form15Year, setForm15Year] = useState(CURRENT_YEAR);
    // Apprentices Act, 1961 - Apprentice/Training Report: From Month/To
    // Month (a month-year picker, like LabourLawFilter.jsx's "Month" filter -
    // the user only ever picks a month + year, never a day), defaulting to
    // the current month but freely changeable to any valid range -
    // internally split month-wise (first day through last day of each
    // month) on the backend.
    const [apprenticesFromDate, setApprenticesFromDate] = useState(
        new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    );
    const [apprenticesToDate, setApprenticesToDate] = useState(
        new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    );
    // Shops & Establishment Act: whichever fields the selected form's
    // `filters` (see Compliances.jsx/ShopsEstablishment.jsx's Link state)
    // actually call for - never every field at once.
    const [seFromDate, setSeFromDate] = useState("");
    const [seToDate, setSeToDate] = useState("");
    const [seMonth, setSeMonth] = useState("");
    const [seYear, setSeYear] = useState("");

    const isPt = form === "pt";
    const isLwf = form === "lwf";
    // Every LWF form configured for Goa/West Bengal/Kerala/Andhra Pradesh/
    // Tamil Nadu/Telangana today IS one of the newly-added forms (no old LWF
    // form exists for these six states), so keying "is this a new form" off
    // the state id alone (NEW_LWF_FORM_SHAPES' keys) is safe and can never
    // match an old LWF state/form - old states (Delhi, Haryana, Gujarat,
    // Maharashtra, Karnataka, Punjab) are completely untouched by everything
    // gated on this flag below.
    const isNewLwfStateForm =
        isLwf && Object.prototype.hasOwnProperty.call(NEW_LWF_FORM_SHAPES, Number(complianceState));
    const newLwfFormShape = isNewLwfStateForm
        ? NEW_LWF_FORM_SHAPES[Number(complianceState)][detectNewLwfFormLetter(type)]
        : null;
    // Register of Wages subset of the new forms (West Bengal Form G, Kerala
    // Form B, Goa Form D) - a single Payroll Month register with an Employee
    // Status + Employee filter.
    const isLwfRegisterOfWagesForm = newLwfFormShape === "month-year";
    // The Half-Year subset (Goa Form A, West Bengal Form D/E, Kerala Form A).
    const isNewLwfHalfYearlyForm = newLwfFormShape === "half-year";
    // The Year-only subset (Andhra Pradesh Form E/F, Tamil Nadu Form A,
    // Telangana Form E/F, Kerala Form C) - no Half-Year, no Employee fields.
    const isNewLwfYearOnlyForm = newLwfFormShape === "year";
    const isForm24Q = form === "form24q";
    const isForm12B = form === "form12b";
    // Minimum Wages ("mw") and Register of Wages / Form XVII ("llf") - both
    // come from real pms_compliance_form rows for the selected state (see
    // Compliances.jsx CARD_META), and both need the same State + Payroll
    // Month this page already collects.
    const isMw = form === "mw";
    const isFormXVII = form === "llf";
    // Form A - Muster Roll (Maternity Benefit Act) - unlike every other
    // report on this page, it is employee-specific rather than state/batch-
    // driven: one employee + the Payroll Month picker already on this page.
    const isMaternityFormA = form === "maternity-form-a";
    // Form C/D (Payment of Bonus Act - Central) and Form F (Payment of
    // Gratuity Act - Central) - all three are Central reports, but every
    // employee they cover must still belong to the State already selected
    // on the Compliance page. Form C/D share the same "Accounting Year"
    // (From Month/To Month); Form C and Form F additionally share the same
    // Employee Status (All/Active/Inactive) + Employee (All Employees/
    // Particular Employee) filter Form 15 also uses below - Form D
    // deliberately has neither, it always covers the whole establishment
    // for the selected range. Unlike PT/LWF/MW/Form XVII, these four never
    // show a State picker on this page at all - see requiresCarriedState
    // below.
    const isBonusFormC = form === "bonus-form-c";
    const isBonusFormD = form === "bonus-form-d";
    const isGratuityFormF = form === "gratuity-form-f";
    const isBonusForm = isBonusFormC || isBonusFormD;
    const isBonusOrGratuity = isBonusForm || isGratuityFormF;
    // Form 15 - Register of Leave with Wages (Factories Act, Section 79) -
    // a plain calendar Year (filed year-wise, Jan-Dec - not a financial
    // year) + the same Employee Status/Employee filter below, plus the same
    // required State Form C/D/F use.
    const isForm15 = form === "form-15";
    // Apprentices Act, 1961 - Apprentice/Training Report: From Month/To
    // Month + Employee Status only (its own standalone Employee Status field below
    // - not the combined Employee Status + Employee picker Form C/F/15 use,
    // since this report has no Employee filter of its own), plus the same
    // required carried State Form C/D/F/15 use.
    const isApprenticesAct = form === "apprentices-act";
    // Shops & Establishment Act - one form key for every state/form
    // combination (never one flag per form, since forms aren't hardcoded
    // here): which filters actually render below comes entirely from
    // `seFilters`, carried from the clicked form's DB-driven config (see
    // Config/shopsEstablishmentForms.js on the backend).
    const isShopsEstablishment = form === "shops-establishment";
    const needsSeEmployeePicker =
        isShopsEstablishment &&
        (seFilters.includes("employee") || seFilters.includes("night_shift_women_employee"));
    // The reports that show the Employee Status + Employee filter pair -
    // Form D is deliberately excluded (see comment above). The Register of
    // Wages LWF forms share this same pair (Employee list scoped to the
    // selected State + Employee Status, exactly like Form C/F/15's), rather
    // than the plain Employee-Status-only dropdown every other LWF form uses.
    const showsEmployeeStatusPicker =
        isBonusFormC || isGratuityFormF || isForm15 || isLwfRegisterOfWagesForm;
    // The reports that show the common State dropdown as a *selectable*
    // field - PT/LWF/MW/Form XVII only. Form C/D/F/15/Apprentices Act's
    // State is already selected on the Compliance page and carried here via
    // `location.state` (see preselectedState/complianceState above) - it
    // must never be re-picked on this Generate page (see requiresCarriedState
    // below). The new LWF forms (Goa/West Bengal/Kerala) work the same way -
    // State is selected once on the Compliance page, never re-picked here -
    // unlike every *old* LWF form, which keeps its own selectable State
    // dropdown on this page exactly as it always has.
    const showsStatePicker = isPt || (isLwf && !isNewLwfStateForm) || isMw || isFormXVII;
    // Form C/D/F/15/Apprentices Act require a State to have already been
    // selected on the Compliance page and carried through via navigation
    // state - there is no picker here to fall back on, so if it's missing
    // the form can't be generated at all (see missingState below). The new
    // LWF forms share this same requirement now that they no longer show
    // their own State dropdown (see showsStatePicker above) - old LWF forms
    // are unaffected, they still show their own dropdown instead.
    const requiresCarriedState =
        isBonusFormC ||
        isBonusFormD ||
        isGratuityFormF ||
        isForm15 ||
        isApprenticesAct ||
        isShopsEstablishment ||
        isNewLwfStateForm;
    const missingState = requiresCarriedState && !complianceState;
    // The period-picker SHAPE every new LWF form needs is fully determined by
    // its own newLwfFormShape above, never by whatever this state's shared
    // pms_compliance_master filing frequency happens to be (a state like
    // Kerala mixes Half-Year, Month-Year and Year-only forms under that one
    // stored frequency, so the real fetched `lwfFrequency` can't be trusted
    // to pick the right shape for every one of its forms). `lwfFrequency`
    // itself is never even fetched for a new form (see the effect below), so
    // this override is the only thing that decides the new forms' shape;
    // every *old* LWF form is unaffected - effectiveLwfFrequency is an exact
    // passthrough of the real fetched `lwfFrequency` for them.
    const effectiveLwfFrequency = isLwfRegisterOfWagesForm
        ? MONTHLY_FREQUENCY
        : isNewLwfHalfYearlyForm
        ? HALF_YEARLY_FREQUENCY
        : isNewLwfYearOnlyForm
        ? YEARLY_FREQUENCY
        : lwfFrequency;
    // Register of Wages LWF forms always use the plain Payroll Month picker
    // (never the Year + Quarter/Half-Year period picker), regardless of
    // whatever filing frequency is configured for the state's *other* LWF
    // forms - see isLwfRegisterOfWagesForm above.
    const needsPeriodPicker =
        isLwf && !isLwfRegisterOfWagesForm && effectiveLwfFrequency && effectiveLwfFrequency !== MONTHLY_FREQUENCY;

    useEffect(() => {
        if (!showsStatePicker) return;

        getStates()
            .then((res) => setStates(res.data?.data || []))
            .catch(() => toast.error("Failed to load states. Please try again."));
    }, [showsStatePicker]);

    // Same tenant-scoped employee list every other "select an employee"
    // picker in the app already uses (see getUsersByStatus -> GET /users) -
    // shared by Form A - Muster Roll and Form C/F/15's "Particular Employee"
    // picker below, rather than a new employee dropdown. The list is
    // re-fetched from the backend every time the selected Employee Status OR
    // the selected State changes, so the dropdown only ever offers employees
    // actually matching both (Active/Inactive/All, and whichever State -
    // resolved through Office Location/Office Location History - is
    // selected/carried) - never fetched once and filtered in the browser.
    // Form A - Muster Roll has no Employee Status filter of its own and
    // always picks from active employees only, exactly as it always has -
    // only its State scoping is new, carried the same way Form C/F/15's is.
    useEffect(() => {
        if (!isMaternityFormA && !showsEmployeeStatusPicker && !needsSeEmployeePicker) return;

        const status = isMaternityFormA ? "active" : employeeStatus;
        const stateId = complianceState || undefined;

        getUsersByStatus(status, stateId)
            .then((res) => setEmployees(res.data?.data || []))
            .catch(() => toast.error("Failed to load employees. Please try again."));
    }, [
        isMaternityFormA,
        showsEmployeeStatusPicker,
        needsSeEmployeePicker,
        employeeStatus,
        complianceState,
    ]);

    // Reset the selected employee whenever the Employee Status or the
    // selected State changes (defaulting back to "All Employees"), so an
    // employee chosen under one status/state is never silently carried over
    // and sent to the backend under a different, now-mismatched status/state.
    useEffect(() => {
        setSelectedEmployee(null);
    }, [employeeStatus, complianceState]);

    // Form 12B needs every employee (active + inactive) to choose from,
    // unlike every other compliance form's Employee Status filter - but the
    // list is still scoped to the State carried from the Compliance page,
    // exactly like Form C/F/15/Form A's Employee picker, so it's re-fetched
    // whenever that State changes too.
    useEffect(() => {
        if (!isForm12B) return;

        getForm12BEmployees(complianceState || undefined)
            .then((res) => setForm12BEmployees(res.data?.data || []))
            .catch(() => toast.error("Failed to load employees. Please try again."));
    }, [isForm12B, complianceState]);

    // Reset any selected Form 12B employees whenever the selected State
    // changes, same reason selectedEmployee is reset below - an employee
    // chosen under one State must never be silently carried over and sent
    // to the backend under a different, now-mismatched State.
    useEffect(() => {
        setSelectedForm12BEmployees([]);
    }, [complianceState]);

    // LWF's filing frequency (monthly/quarterly/half-yearly/yearly) is
    // configured per state in pms_compliance_master; fetch it whenever the
    // state is known so the right period picker can be shown below. Skipped
    // entirely for the new LWF forms - their period-picker shape always
    // comes from effectiveLwfFrequency's own per-form override above, never
    // from this state-level lookup (which, for a mixed state like Kerala,
    // couldn't tell apart its Half-Year/Month-Year/Year-only forms anyway) -
    // so there's nothing useful to fetch, and skipping it also avoids a
    // spurious "failed to load" toast for a new state with no configured
    // frequency master row at all (e.g. Tamil Nadu, Telangana).
    useEffect(() => {
        if (!isLwf || !complianceState || isNewLwfStateForm) {
            setLwfFrequency("");
            return;
        }

        getComplianceFrequency("LWF", complianceState)
            .then((res) => setLwfFrequency(res.data?.data?.frequency || ""))
            .catch((error) => {
                setLwfFrequency("");
                toast.error(
                    error.response?.data?.message ||
                        "Failed to load LWF compliance configuration for the selected state."
                );
            });
    }, [isLwf, complianceState, isNewLwfStateForm]);

    // Reset the period selection whenever the effective frequency changes so
    // a quarter/half picked for one state/form doesn't leak into another.
    useEffect(() => {
        setPeriod({ year: "", quarter: "", half: "" });
    }, [effectiveLwfFrequency]);

    // Shared by both generation methods below: turns the blob response into
    // a file download, or reports the failure.
    const downloadOrReportFailure = (response, filename) => {
        if (response?.status === 200) {
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();

            setTimeout(() => URL.revokeObjectURL(url), 100);
            toast.success("Compliance document generated successfully!");
        } else {
            toast.error("Failed to generate compliance document. Please try again.");
        }
    };

    // Form A - Muster Roll (Maternity Benefit Act): one employee + the
    // Payroll Month already selected on this page. The form itself is the
    // same statutory layout for every employee/state - only the employee
    // and month change what the backend fills it in with.
    const handleGenerateMaternityFormA = async () => {
        if (!selectedEmployee) {
            toast.error("Please select an Employee.");
            return;
        }
        if (!monthYear) {
            toast.error("Please select a Payroll Month.");
            return;
        }

        try {
            showLoader();
            const response = await generateMaternityFormA({
                employee_id: selectedEmployee._UserID,
                payroll_month: monthYear,
            });

            downloadOrReportFailure(
                response,
                `FormA_MusterRoll_${selectedEmployee._UserEmployeeID}_${monthYear}.xlsx`
            );
        } catch (error) {
            toast.error(
                error.response?.data?.message ||
                    "Error generating Form A - Muster Roll. Please try again."
            );
        } finally {
            hideLoader();
        }
    };

    // Bonus Form C/D (Payment of Bonus Act) and Gratuity Form F (Payment of
    // Gratuity Act) share this one handler, but their fields diverge: Form
    // C/D both need From Month/To Month; Form C and Form F both need
    // Employee Status + Employee (`selectedEmployee` is either
    // null/ALL_EMPLOYEES_OPTION -> "All Employees", or a specific employee
    // object); Form D needs neither Employee field and always covers the
    // whole establishment.
    const handleGenerateBonusOrGratuity = async () => {
        const isAllEmployees = !selectedEmployee || selectedEmployee._UserID === "ALL";

        if (missingState) {
            toast.error(
                "No State was selected on the Compliance page. Please go back and select a State."
            );
            return;
        }

        if (isBonusForm && (!fromMonth || !toMonth)) {
            toast.error("Please select From Month and To Month.");
            return;
        }
        if (isBonusForm && fromMonth > toMonth) {
            toast.error("From Month cannot be after To Month.");
            return;
        }

        try {
            showLoader();
            let response;
            let filename;

            if (isGratuityFormF) {
                const payload = {
                    employee_status: employeeStatus,
                    state: complianceState,
                    ...(isAllEmployees
                        ? { all_employees: true }
                        : { employee_id: selectedEmployee._UserID }),
                };
                response = await generateGratuityFormF(payload);
                filename = isAllEmployees
                    ? "Gratuity_Form_F_All_Employees.xlsx"
                    : `Gratuity_Form_F_${selectedEmployee._UserEmployeeID}.xlsx`;
            } else if (isBonusFormC) {
                const payload = {
                    from_month: fromMonth,
                    to_month: toMonth,
                    employee_status: employeeStatus,
                    state: complianceState,
                    ...(!isAllEmployees && { employee_id: selectedEmployee._UserID }),
                };
                response = await generateBonusFormC(payload);
                filename = `Bonus_Form_C_${fromMonth}_to_${toMonth}${
                    isAllEmployees ? "" : `_${selectedEmployee._UserEmployeeID}`
                }.xlsx`;
            } else {
                // Bonus Form D: From Month/To Month + required State only -
                // no Employee or Employee Status field is ever sent.
                const payload = {
                    from_month: fromMonth,
                    to_month: toMonth,
                    state: complianceState,
                };
                response = await generateBonusFormD(payload);
                filename = `Bonus_Form_D_${fromMonth}_to_${toMonth}.xlsx`;
            }

            downloadOrReportFailure(response, filename);
        } catch (error) {
            if (error.response?.status === 404) {
                toast.error("No data found for the selected criteria.");
            } else {
                toast.error(
                    error.response?.data?.message ||
                        "Error generating compliance document. Please try again."
                );
            }
        } finally {
            hideLoader();
        }
    };

    // Form 15 - Register of Leave with Wages: a plain calendar Year (Jan-Dec,
    // not a financial year) + the same Employee Status/Employee filter Form
    // C/Form F use, plus the same optional common State filter those two
    // use - common for every state by default; State narrows the result
    // only when one is actually selected.
    const handleGenerateForm15 = async () => {
        const isAllEmployees = !selectedEmployee || selectedEmployee._UserID === "ALL";

        if (missingState) {
            toast.error(
                "No State was selected on the Compliance page. Please go back and select a State."
            );
            return;
        }

        if (!form15Year) {
            toast.error("Please select a Year.");
            return;
        }

        try {
            showLoader();
            const payload = {
                year: form15Year,
                employee_status: employeeStatus,
                state: complianceState,
                ...(!isAllEmployees && { employee_id: selectedEmployee._UserID }),
            };
            const response = await generateForm15(payload);
            const filename = `Form_15_Register_of_Leave_with_Wages_${form15Year}${
                isAllEmployees ? "" : `_${selectedEmployee._UserEmployeeID}`
            }.xlsx`;

            downloadOrReportFailure(response, filename);
        } catch (error) {
            if (error.response?.status === 404) {
                toast.error("No employees found for the selected filters.");
            } else {
                toast.error(
                    error.response?.data?.message ||
                        "Error generating Form 15 - Register of Leave with Wages. Please try again."
                );
            }
        } finally {
            hideLoader();
        }
    };

    // Apprentices Act, 1961 - Apprentice/Training Report: From Month/To
    // Month + Employee Status, State carried from the Compliance page - same
    // shape/behaviour as handleGenerateForm15 above (one Generate button,
    // direct Excel download, no separate on-screen preview or Export
    // button), just with a month range instead of a plain Year. A
    // zero-match result is not treated as a failure here - the backend
    // always returns a downloadable report, with a "No records found" row
    // in place of data, so there's no 404 branch to special-case below.
    const handleGenerateApprenticesAct = async () => {
        if (missingState) {
            toast.error(
                "No State was selected on the Compliance page. Please go back and select a State."
            );
            return;
        }

        if (!apprenticesFromDate || !apprenticesToDate) {
            toast.error("Please select From Month and To Month.");
            return;
        }
        if (apprenticesFromDate > apprenticesToDate) {
            toast.error("From Month cannot be after To Month.");
            return;
        }

        try {
            showLoader();
            const payload = {
                from_date: formatDateToYYYYMMDD(apprenticesFromDate),
                to_date: formatDateToYYYYMMDD(apprenticesToDate),
                employee_status: employeeStatus,
                state: complianceState,
            };
            const response = await generateApprenticesAct(payload);
            const filename = `Apprentices_Act_Report_${payload.from_date}_to_${payload.to_date}.xlsx`;

            downloadOrReportFailure(response, filename);
        } catch (error) {
            toast.error(
                error.response?.data?.message ||
                    "Error generating the Apprentices Act report. Please try again."
            );
        } finally {
            hideLoader();
        }
    };

    // Shops & Establishment Act: Phase 1 only generates the blank report
    // template (see Services/compliance.service.js generateShopsEstablishmentReport
    // on the backend) - whichever filters the selected form needs (from
    // `seFilters`) are validated and sent, but no employee/leave/payroll
    // data is fetched yet.
    const handleGenerateShopsEstablishment = async () => {
        if (missingState) {
            toast.error(
                "No State was selected for this form. Please go back and open it again."
            );
            return;
        }
        if (seFilters.includes("from_date") && !seFromDate) {
            toast.error("Please select From Date.");
            return;
        }
        if (seFilters.includes("to_date") && !seToDate) {
            toast.error("Please select To Date.");
            return;
        }
        if (seFromDate && seToDate && seFromDate > seToDate) {
            toast.error("From Date cannot be after To Date.");
            return;
        }
        if (seFilters.includes("month") && !seMonth) {
            toast.error("Please select Month & Year.");
            return;
        }
        if (seFilters.includes("year") && !seYear) {
            toast.error("Please select a Year.");
            return;
        }
        if (seFilters.includes("night_shift_women_employee") && !selectedEmployee) {
            toast.error("Please select the Night Shift Women Employee.");
            return;
        }

        const isAllEmployees = !selectedEmployee || selectedEmployee._UserID === "ALL";

        try {
            showLoader();
            const payload = {
                form_id: formId,
                state: complianceState,
                ...(seFilters.includes("from_date") && { from_date: seFromDate }),
                ...(seFilters.includes("to_date") && { to_date: seToDate }),
                ...(seFilters.includes("month") && { month: seMonth }),
                ...(seFilters.includes("year") && { year: seYear }),
                ...(seFilters.includes("employee") && {
                    employee_status: employeeStatus,
                    ...(!isAllEmployees && { employee_id: selectedEmployee._UserID }),
                }),
                ...(seFilters.includes("night_shift_women_employee") && {
                    employee_id: selectedEmployee?._UserID,
                }),
            };

            const response = await generateShopsEstablishmentForm(payload);
            downloadOrReportFailure(response, `${(label || "Shops_Establishment_Report").replace(/\s+/g, "_")}.xlsx`);
        } catch (error) {
            toast.error(
                error.response?.data?.message ||
                    "Error generating the Shops & Establishment report. Please try again."
            );
        } finally {
            hideLoader();
        }
    };

    // "Older" LWF generation method - the dynamic per-employee report built
    // from real payroll deduction data (employee + employer contribution).
    // This is also the only generation method PT uses.
    const handleGenerateReport = async (e) => {
        e.preventDefault();

        // Form A - Muster Roll, Bonus/Gratuity and Form 15 each have their
        // own employee-specific flow above - none of them use the State/
        // Employee Status/period-picker validation and payload the rest of
        // this function builds for PT/LWF/MW/Form XVII/Form 24Q.
        if (isMaternityFormA) {
            return handleGenerateMaternityFormA();
        }
        if (isBonusOrGratuity) {
            return handleGenerateBonusOrGratuity();
        }
        if (isForm15) {
            return handleGenerateForm15();
        }
        if (isApprenticesAct) {
            return handleGenerateApprenticesAct();
        }
        if (isShopsEstablishment) {
            return handleGenerateShopsEstablishment();
        }

        // The period picker (Year + Quarter/Half-Year) replaces the Payroll
        // Month for quarterly/half-yearly/yearly LWF - the whole period's
        // data is generated, not just whatever month happens to be selected
        // in the Payroll Month filter above. Form 24Q and Form 12B always
        // use their own Year picker below, never the Payroll Month filter.
        if (!needsPeriodPicker && !isForm24Q && !isForm12B && !isBonusOrGratuity && !isForm15 && !monthYear) {
            toast.error("Please select a Payroll Month.");
            return;
        }

        if ((isPt || isLwf || isMw || isFormXVII) && !complianceState) {
            toast.error("Please select a State.");
            return;
        }

        if (!isForm12B && !isBonusOrGratuity && !isForm15 && !employeeStatus) {
            toast.error("Please select an Employee Status.");
            return;
        }

        if (isForm12B) {
            if (!period.year) {
                toast.error("Please select a Year.");
                return;
            }
            if (!selectedForm12BEmployees.length) {
                toast.error("Please select at least one Employee.");
                return;
            }
        }

        if (needsPeriodPicker) {
            if (!period.year) {
                toast.error("Please select a Year.");
                return;
            }
            if (lwfFrequency === QUARTERLY_FREQUENCY && !period.quarter) {
                toast.error("Please select a Quarter.");
                return;
            }
            if (lwfFrequency === HALF_YEARLY_FREQUENCY && !period.half) {
                toast.error("Please select a Half-Year.");
                return;
            }
        }

        if (isForm24Q) {
            if (!period.year) {
                toast.error("Please select a Financial Year.");
                return;
            }
            if (!period.quarter) {
                toast.error("Please select a Quarter.");
                return;
            }
        }

        // PT now generates the statutory "Statement of Profession Tax"
        // (letterhead + rate summary + sign-off) via its own endpoint; Form
        // 24Q generates its own quarterly, month-wise-sheet TDS statement;
        // LWF still uses the plain tabular export.
        const payload = isPt
            ? {
                  payroll_month: monthYear,
                  state: complianceState,
                  employee_status: employeeStatus,
              }
            : isForm24Q
            ? {
                  year: period.year,
                  quarter: period.quarter,
                  employee_status: employeeStatus,
              }
            : isForm12B
            ? {
                  year: period.year,
                  employee_ids: selectedForm12BEmployees.map((e) => e.id),
              }
            : needsPeriodPicker
            ? {
                  form,
                  type,
                  state: complianceState,
                  employee_status: employeeStatus,
                  year: period.year,
                  ...(lwfFrequency === QUARTERLY_FREQUENCY && { quarter: period.quarter }),
                  ...(lwfFrequency === HALF_YEARLY_FREQUENCY && { half_year: period.half }),
              }
            : {
                  form,
                  type,
                  payroll_month: monthYear,
                  state: complianceState,
                  employee_status: employeeStatus,
              };

        try {
            showLoader();
            const response = isPt
                ? await generateProfessionTaxStatement(payload)
                : isForm24Q
                ? await generateForm24Q(payload)
                : isForm12B
                ? await generateForm12B(payload)
                : isFormXVII
                ? await generateFormXVII(payload)
                : // Minimum Wages ("mw") falls through to the same
                  // generateCompliance() call as plain LWF - it's handled by
                  // ComplianceService.generateComplianceData on the backend,
                  // reached through the exact same "/generate" endpoint.
                  await generateCompliance(payload);

            const periodLabel = isForm24Q
                ? `FY${period.year}-${String(Number(period.year) + 1).slice(-2)}_Q${period.quarter}`
                : isForm12B
                ? period.year
                : needsPeriodPicker
                ? [
                      period.year,
                      lwfFrequency === QUARTERLY_FREQUENCY && `Q${period.quarter}`,
                      lwfFrequency === HALF_YEARLY_FREQUENCY && `H${period.half}`,
                  ]
                      .filter(Boolean)
                      .join("_")
                : monthYear;

            downloadOrReportFailure(response, `${form}_${type}_${periodLabel}.xlsx`);
        } catch (error) {
            toast.error(
                error.response?.data?.message ||
                    "Error generating compliance document. Please try again."
            );
        } finally {
            hideLoader();
        }
    };

    // "Newer" LWF generation method - the statutory Form A/B state letterhead
    // template. Only the State is strictly required, but it also forwards
    // whichever payroll period (Payroll Month, or Year + Quarter/Half-Year)
    // and Employee Status are already selected above - the same fields the
    // deduction report sends - so the backend can fill the template in with
    // real payroll data for that period instead of static sample values.
    const handleGenerateStatutoryForm = async (e) => {
        e.preventDefault();

        if (!complianceState) {
            toast.error("Please select a State.");
            return;
        }

        // Same requirement as handleGenerateReport above - without it, the
        // request goes out with no year and no payroll_month, the backend
        // resolves the period to zero months, and the statutory form
        // silently renders with an empty employees list instead of erroring.
        if (needsPeriodPicker) {
            if (!period.year) {
                toast.error("Please select a Year.");
                return;
            }
            if (effectiveLwfFrequency === QUARTERLY_FREQUENCY && !period.quarter) {
                toast.error("Please select a Quarter.");
                return;
            }
            if (effectiveLwfFrequency === HALF_YEARLY_FREQUENCY && !period.half) {
                toast.error("Please select a Half-Year.");
                return;
            }
        } else if (!monthYear) {
            toast.error("Please select a Payroll Month.");
            return;
        }

        // Register of Wages forms' "Particular Employee" option -
        // `selectedEmployee` is null/ALL_EMPLOYEES_OPTION for "All Employees"
        // (see employeeStatusAndPickerFields below), exactly like Form C/F/15.
        const isAllEmployees = !selectedEmployee || selectedEmployee._UserID === "ALL";

        const payload = {
            state: complianceState,
            type,
            employee_status: employeeStatus,
            ...(needsPeriodPicker
                ? {
                      year: period.year,
                      ...(effectiveLwfFrequency === QUARTERLY_FREQUENCY &&
                          period.quarter && { quarter: period.quarter }),
                      ...(effectiveLwfFrequency === HALF_YEARLY_FREQUENCY &&
                          period.half && { half_year: period.half }),
                  }
                : { payroll_month: monthYear }),
            ...(isLwfRegisterOfWagesForm &&
                !isAllEmployees && { employee_id: selectedEmployee._UserID }),
        };

        try {
            showLoader();
            const response = await generateLwfStatutoryForm(payload);
            downloadOrReportFailure(response, `${form}_${type}_statutory_form.xlsx`);
        } catch (error) {
            toast.error(
                error.response?.data?.message ||
                    "Error generating LWF statutory form. Please try again."
            );
        } finally {
            hideLoader();
        }
    };

    // Shared Employee Status + Employee fields - Form C, Form F, Form 15 and
    // the Register of Wages LWF forms (West Bengal Form G, Kerala Form B,
    // Goa Form D) all render this exact same pair (Bonus Form D never does,
    // see showsEmployeeStatusPicker above), defined once here and dropped
    // into whichever row layout each report needs below instead of being
    // duplicated.
    const employeeStatusAndPickerFields = (
        <>
            <div className="col-lg-4 col-md-6 mb-3">
                <label htmlFor="complianceEmployeeStatus" className="me-2 fw-bold">
                    Employee Status:
                </label>

                <select
                    id="complianceEmployeeStatus"
                    name="complianceEmployeeStatus"
                    className="form-control"
                    value={employeeStatus}
                    onChange={(e) => setEmployeeStatus(e.target.value)}
                >
                    {EMPLOYEE_STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className="col-lg-4 col-md-6 mb-3">
                <label htmlFor="complianceEmployee" className="me-2 fw-bold d-block">
                    Employee:
                </label>

                <Autocomplete
                    id="complianceEmployee"
                    options={[ALL_EMPLOYEES_OPTION, ...employees]}
                    value={selectedEmployee || ALL_EMPLOYEES_OPTION}
                    getOptionLabel={(o) =>
                        !o || o._UserID === "ALL"
                            ? "All Employees"
                            : `${[o._FirstName, o._MiddleName, o._LastName].filter(Boolean).join(" ")} (${o._UserEmployeeID})`
                    }
                    isOptionEqualToValue={(o, v) => o._UserID === v._UserID}
                    onChange={(e, val) =>
                        setSelectedEmployee(val && val._UserID !== "ALL" ? val : null)
                    }
                    renderInput={(params) => (
                        <TextField {...params} size="small" />
                    )}
                />
            </div>
        </>
    );

    return (
        <div>
            <h1>Generate {label}</h1>

            <form onSubmit={handleGenerateReport}>
                {/* The one common State dropdown PT/LWF share - required and
                    still user-selectable here, unchanged. Form C/D/F/15
                    deliberately do NOT get this picker: their State was
                    already selected on the Compliance page and is carried
                    into `complianceState` via location.state (see
                    preselectedState above) - showing/re-picking it here
                    would let it silently diverge from the Compliance page's
                    selection, which is exactly what must not happen. The new
                    LWF forms (Goa/West Bengal/Kerala) join that same "no
                    re-picking" group now - see showsStatePicker/
                    requiresCarriedState above - every *old* LWF form keeps
                    this dropdown exactly as before. */}
                {(isPt || (isLwf && !isNewLwfStateForm)) && (
                    <div className="row mb-3">
                        <div className="col-lg-4 col-md-6 mb-3">
                            <label htmlFor="state" className="me-2 fw-bold">
                                State:
                            </label>

                            <select
                                id="state"
                                name="state"
                                className="form-control"
                                value={complianceState}
                                onChange={(e) => setComplianceState(e.target.value)}
                            >
                                <option value="">Select State</option>
                                {states.map((s) => (
                                    <option key={s._ZoneID} value={s._ZoneID}>
                                        {s._Name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {/* Form C/D/F/15: no State field on this page at all (see
                    above) - if it's missing (e.g. this page was opened
                    without going through the Compliance page's State
                    selector first), the report can't be scoped to a state
                    at all, so block generation and say why instead of
                    silently running across every state. */}
                {missingState && (
                    <div className="alert alert-danger" role="alert">
                        No State was selected on the Compliance page. Please go back to the
                        Compliance page, select a State, and open this report again.
                    </div>
                )}

                {/* Bonus Form C/D's From Month/To Month range. Form D stops
                    here - it has no Employee or Employee Status field at
                    all (see showsEmployeeStatusPicker above). */}
                {isBonusForm && (
                    <div className="row mb-3">
                        <div className="col-lg-4 col-md-6 mb-3">
                            <label htmlFor="fromMonth" className="me-2 fw-bold">
                                From Month:
                            </label>

                            <input
                                type="month"
                                id="fromMonth"
                                name="fromMonth"
                                className="form-control"
                                value={fromMonth}
                                onChange={(e) => setFromMonth(e.target.value)}
                            />
                        </div>

                        <div className="col-lg-4 col-md-6 mb-3">
                            <label htmlFor="toMonth" className="me-2 fw-bold">
                                To Month:
                            </label>

                            <input
                                type="month"
                                id="toMonth"
                                name="toMonth"
                                className="form-control"
                                value={toMonth}
                                onChange={(e) => setToMonth(e.target.value)}
                            />
                        </div>
                    </div>
                )}

                {/* Form 15 - Register of Leave with Wages: a plain calendar
                    Year (Jan-Dec, not a financial year - reusing the same
                    YEAR_OPTIONS Form 12B's Year dropdown already uses),
                    sharing one row with the Employee Status + Employee
                    fields below - common for every state, so no State field
                    is shown here at all. */}
                {isForm15 && (
                    <div className="row mb-3">
                        <div className="col-lg-4 col-md-6 mb-3">
                            <label htmlFor="form15Year" className="me-2 fw-bold">
                                Select Year:
                            </label>

                            <select
                                id="form15Year"
                                name="form15Year"
                                className="form-control"
                                value={form15Year}
                                onChange={(e) => setForm15Year(e.target.value)}
                            >
                                <option value="">Select Year</option>
                                {YEAR_OPTIONS.map((y) => (
                                    <option key={y} value={y}>
                                        {y}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {employeeStatusAndPickerFields}
                    </div>
                )}

                {/* Apprentices Act, 1961 - Apprentice/Training Report: From
                    Month/To Month (a month-year picker - the same
                    views={["year", "month"]} MUI DatePicker configuration
                    LabourLawFilter.jsx's "Month" filter already uses, so the
                    user only ever picks a month + year, never a day) + a
                    standalone Employee Status field - no Employee/State/
                    Office Location/Department/Employee Type field at all,
                    unlike Form 15/C/F above. */}
                {isApprenticesAct && (
                    <div className="row mb-3">
                        <div className="col-lg-4 col-md-6 mb-3">
                            <label htmlFor="apprenticesFromDate" className="me-2 fw-bold d-block">
                                From Month *
                            </label>

                            <DatePicker
                                value={apprenticesFromDate}
                                onChange={(val) => setApprenticesFromDate(val)}
                                views={["year", "month"]}
                                openTo="month"
                                format="MMM yyyy"
                                slotProps={{
                                    textField: {
                                        id: "apprenticesFromDate",
                                        size: "small",
                                        fullWidth: true,
                                        required: true,
                                    },
                                    actionBar: {
                                        actions: ["clear", "today"],
                                    },
                                }}
                            />
                        </div>

                        <div className="col-lg-4 col-md-6 mb-3">
                            <label htmlFor="apprenticesToDate" className="me-2 fw-bold d-block">
                                To Month *
                            </label>

                            <DatePicker
                                value={apprenticesToDate}
                                onChange={(val) => setApprenticesToDate(val)}
                                views={["year", "month"]}
                                openTo="month"
                                format="MMM yyyy"
                                slotProps={{
                                    textField: {
                                        id: "apprenticesToDate",
                                        size: "small",
                                        fullWidth: true,
                                        required: true,
                                    },
                                    actionBar: {
                                        actions: ["clear", "today"],
                                    },
                                }}
                            />
                        </div>

                        <div className="col-lg-4 col-md-6 mb-3">
                            <label htmlFor="apprenticesEmployeeStatus" className="me-2 fw-bold">
                                Employee Status:
                            </label>

                            <select
                                id="apprenticesEmployeeStatus"
                                name="apprenticesEmployeeStatus"
                                className="form-control"
                                value={employeeStatus}
                                onChange={(e) => setEmployeeStatus(e.target.value)}
                            >
                                {EMPLOYEE_STATUS_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {/* Form C and Gratuity Form F's Employee Status + Employee
                    row (Form 15 gets the same two fields folded into its
                    Year row above instead). The Register of Wages LWF forms
                    (West Bengal Form G, Kerala Form B, Goa Form D) share this
                    same pair, scoped to whichever State is selected via the
                    LWF State dropdown above. */}
                {(isBonusFormC || isGratuityFormF || isLwfRegisterOfWagesForm) && (
                    <div className="row mb-3">{employeeStatusAndPickerFields}</div>
                )}

                {/* Shops & Establishment Act: only the fields the selected
                    form's `filters` actually call for (see
                    Config/shopsEstablishmentForms.js on the backend) - never
                    every field for every form. State is carried from the
                    card clicked on the Compliance page (see missingState
                    alert above), never re-picked here. */}
                {isShopsEstablishment && (
                    <div className="row mb-3">
                        {seFilters.includes("from_date") && (
                            <div className="col-lg-4 col-md-6 mb-3">
                                <label htmlFor="seFromDate" className="me-2 fw-bold">
                                    From Date:
                                </label>

                                <input
                                    type="date"
                                    id="seFromDate"
                                    name="seFromDate"
                                    className="form-control"
                                    value={seFromDate}
                                    onChange={(e) => setSeFromDate(e.target.value)}
                                />
                            </div>
                        )}

                        {seFilters.includes("to_date") && (
                            <div className="col-lg-4 col-md-6 mb-3">
                                <label htmlFor="seToDate" className="me-2 fw-bold">
                                    To Date:
                                </label>

                                <input
                                    type="date"
                                    id="seToDate"
                                    name="seToDate"
                                    className="form-control"
                                    value={seToDate}
                                    onChange={(e) => setSeToDate(e.target.value)}
                                />
                            </div>
                        )}

                        {seFilters.includes("month") && (
                            <div className="col-lg-4 col-md-6 mb-3">
                                <label htmlFor="seMonth" className="me-2 fw-bold">
                                    Month &amp; Year:
                                </label>

                                <input
                                    type="month"
                                    id="seMonth"
                                    name="seMonth"
                                    className="form-control"
                                    value={seMonth}
                                    onChange={(e) => setSeMonth(e.target.value)}
                                />
                            </div>
                        )}

                        {seFilters.includes("year") && (
                            <div className="col-lg-4 col-md-6 mb-3">
                                <label htmlFor="seYear" className="me-2 fw-bold">
                                    Year:
                                </label>

                                <select
                                    id="seYear"
                                    name="seYear"
                                    className="form-control"
                                    value={seYear}
                                    onChange={(e) => setSeYear(e.target.value)}
                                >
                                    <option value="">Select Year</option>
                                    {YEAR_OPTIONS.map((y) => (
                                        <option key={y} value={y}>
                                            {y}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {seFilters.includes("employee") && employeeStatusAndPickerFields}

                        {seFilters.includes("night_shift_women_employee") && (
                            <div className="col-lg-4 col-md-6 mb-3">
                                <label
                                    htmlFor="seNightShiftWomenEmployee"
                                    className="me-2 fw-bold d-block"
                                >
                                    Night Shift Women Employee:
                                </label>

                                <Autocomplete
                                    id="seNightShiftWomenEmployee"
                                    options={employees}
                                    value={selectedEmployee}
                                    getOptionLabel={(o) =>
                                        o
                                            ? `${[o._FirstName, o._MiddleName, o._LastName].filter(Boolean).join(" ")} (${o._UserEmployeeID})`
                                            : ""
                                    }
                                    isOptionEqualToValue={(o, v) => o._UserID === v._UserID}
                                    onChange={(e, val) => setSelectedEmployee(val)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Search Employee Name / Employee Code"
                                            size="small"
                                        />
                                    )}
                                />
                            </div>
                        )}
                    </div>
                )}

                {isMaternityFormA && (
                    <div className="row mb-3">
                        <div className="col-lg-4 col-md-6 mb-3">
                            <Autocomplete
                                options={employees}
                                value={selectedEmployee}
                                getOptionLabel={(o) =>
                                    o
                                        ? `${[o._FirstName, o._MiddleName, o._LastName].filter(Boolean).join(" ")} (${o._UserEmployeeID})`
                                        : ""
                                }
                                isOptionEqualToValue={(o, v) => o._UserID === v._UserID}
                                onChange={(e, val) => setSelectedEmployee(val)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search Employee Name / Employee Code"
                                        size="small"
                                    />
                                )}
                            />
                        </div>
                    </div>
                )}

                {needsPeriodPicker && (
                    <div className="row mb-3">
                        <div className="col-lg-4 col-md-6 mb-3">
                            <label htmlFor="year" className="me-2 fw-bold">
                                Year:
                            </label>

                            <select
                                id="year"
                                name="year"
                                className="form-control"
                                value={period.year}
                                onChange={(e) =>
                                    setPeriod((p) => ({ ...p, year: e.target.value }))
                                }
                            >
                                <option value="">Select Year</option>
                                {YEAR_OPTIONS.map((y) => (
                                    <option key={y} value={y}>
                                        {y}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {effectiveLwfFrequency === QUARTERLY_FREQUENCY && (
                            <div className="col-lg-4 col-md-6 mb-3">
                                <label htmlFor="quarter" className="me-2 fw-bold">
                                    Quarter:
                                </label>

                                <select
                                    id="quarter"
                                    name="quarter"
                                    className="form-control"
                                    value={period.quarter}
                                    onChange={(e) =>
                                        setPeriod((p) => ({ ...p, quarter: e.target.value }))
                                    }
                                >
                                    <option value="">Select Quarter</option>
                                    {QUARTER_OPTIONS.map((q) => (
                                        <option key={q.value} value={q.value}>
                                            {q.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {effectiveLwfFrequency === HALF_YEARLY_FREQUENCY && (
                            <div className="col-lg-4 col-md-6 mb-3">
                                <label htmlFor="half" className="me-2 fw-bold">
                                    Half-Year:
                                </label>

                                <select
                                    id="half"
                                    name="half"
                                    className="form-control"
                                    value={period.half}
                                    onChange={(e) =>
                                        setPeriod((p) => ({ ...p, half: e.target.value }))
                                    }
                                >
                                    <option value="">Select Half-Year</option>
                                    {HALF_YEAR_OPTIONS.map((h) => (
                                        <option key={h.value} value={h.value}>
                                            {h.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                )}

                {isForm24Q && (
                    <div className="row mb-3">
                        <div className="col-lg-4 col-md-6 mb-3">
                            <label htmlFor="form24qYear" className="me-2 fw-bold">
                                Financial Year:
                            </label>

                            <select
                                id="form24qYear"
                                name="form24qYear"
                                className="form-control"
                                value={period.year}
                                onChange={(e) =>
                                    setPeriod((p) => ({ ...p, year: e.target.value }))
                                }
                            >
                                <option value="">Select Year</option>
                                {YEAR_OPTIONS.map((y) => (
                                    <option key={y} value={y}>
                                        {y} - {String(y + 1).slice(-2)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-lg-4 col-md-6 mb-3">
                            <label htmlFor="form24qQuarter" className="me-2 fw-bold">
                                Quarter:
                            </label>

                            <select
                                id="form24qQuarter"
                                name="form24qQuarter"
                                className="form-control"
                                value={period.quarter}
                                onChange={(e) =>
                                    setPeriod((p) => ({ ...p, quarter: e.target.value }))
                                }
                            >
                                <option value="">Select Quarter</option>
                                {FORM24Q_QUARTER_OPTIONS.map((q) => (
                                    <option key={q.value} value={q.value}>
                                        {q.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {isForm12B && (
                    <div className="row mb-3">
                        <div className="col-lg-4 col-md-6 mb-3">
                            <label htmlFor="form12bYear" className="me-2 fw-bold">
                                Year:
                            </label>

                            <select
                                id="form12bYear"
                                name="form12bYear"
                                className="form-control"
                                value={period.year}
                                onChange={(e) =>
                                    setPeriod((p) => ({ ...p, year: e.target.value }))
                                }
                            >
                                <option value="">Select Year</option>
                                {YEAR_OPTIONS.map((y) => (
                                    <option key={y} value={y}>
                                        {y}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-lg-4 col-md-6 mb-3">
                            <label htmlFor="form12bEmployees" className="me-2 fw-bold">
                                Employees:
                            </label>

                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                id="form12bEmployees"
                                size="small"
                                options={form12BEmployees}
                                value={selectedForm12BEmployees}
                                onChange={(e, value) => setSelectedForm12BEmployees(value)}
                                getOptionLabel={(o) => `${o.name} (${o.employeeCode})`}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="Select Employees" />
                                )}
                            />
                        </div>
                    </div>
                )}

                {/* The new LWF forms never show this plain Employee-Status-
                    only dropdown: the Register of Wages ones (West Bengal
                    Form G, Kerala Form B, Goa Form D) get the fuller Employee
                    Status + Employee pair below instead, and the half-yearly
                    ones (Goa Form A, West Bengal Form D/E, Kerala Form A)
                    take no Employee Status field at all - Year + Half-Year
                    only. Every *old* LWF form is unaffected and keeps this
                    dropdown exactly as before. */}
                {!isForm12B &&
                    !isMaternityFormA &&
                    !isBonusOrGratuity &&
                    !isForm15 &&
                    !isApprenticesAct &&
                    !isShopsEstablishment &&
                    !isNewLwfStateForm && (
                        <div className="row mb-3">
                            <div className="col-lg-4 col-md-6 mb-3">
                                <label htmlFor="employeeStatus" className="me-2 fw-bold">
                                    Employee Status:
                                </label>

                                <select
                                    id="employeeStatus"
                                    name="employeeStatus"
                                    className="form-control"
                                    value={employeeStatus}
                                    onChange={(e) => setEmployeeStatus(e.target.value)}
                                >
                                    <option value="">Select Status</option>
                                    {EMPLOYEE_STATUS_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                {/* The new LWF forms (Goa/West Bengal/Kerala) get exactly ONE
                    "Generate Report" button - there is no separate "Generate
                    Deduction Report" flow for them, unlike every *old* LWF
                    form below, which keeps both its buttons exactly as
                    before. */}
                {isNewLwfStateForm ? (
                    <Button
                        type="button"
                        variant="primary"
                        onClick={handleGenerateStatutoryForm}
                        disabled={missingState}
                    >
                        Generate Report
                    </Button>
                ) : isLwf ? (
                    <div className="d-flex gap-2">
                        <Button
                            type="button"
                            variant="primary"
                            onClick={handleGenerateStatutoryForm}
                        >
                            Generate Statutory Form
                        </Button>
                        <Button type="submit" variant="secondary">
                            Generate Deduction Report
                        </Button>
                    </div>
                ) : (
                    <Button type="submit" variant="primary" disabled={missingState}>
                        Generate
                    </Button>
                )}
            </form>
        </div>
    );
};

export default ComplianceGenerate;
